import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Circle,
  GoogleMap,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { GeofenceShapeType, LatLng } from '@models/geofence.model';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
  GOOGLE_MAPS_VERSION,
} from '@utils/googleMapsConfig';
import { DEFAULT_GEOFENCE_COLOR } from '@utils/geofenceCategoryIcons';
import RoomIcon from '@mui/icons-material/Room';
import PolylineIcon from '@mui/icons-material/Polyline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const NEW_SHAPE_ZOOM = 14;

export interface DrawnShapeValue {
  shapeType: GeofenceShapeType;
  centerPoint?: LatLng;
  radiusMeters?: number;
  polygonCoordinates?: LatLng[];
}

interface GeofenceDrawingMapProps {
  shapeType: GeofenceShapeType;
  // Initial value when editing
  initialValue?: DrawnShapeValue;
  // Color used for the shape stroke/fill
  color?: string;
  // Suggested center to focus the map (e.g. project lat/long); used when no initialValue
  suggestedCenter?: LatLng;
  // When this prop changes (new identity), the map pans to it. Used for address search +
  // reactive centering on project/warehouse selection.
  panTo?: LatLng | null;
  onChange: (value: DrawnShapeValue | null) => void;
  size?: { width: string; height: string };
}

const buildPolygonOptions = (color: string, editable: boolean) => ({
  fillColor: color,
  fillOpacity: 0.3,
  strokeColor: color,
  strokeOpacity: 0.9,
  strokeWeight: 2,
  editable,
  draggable: editable,
  clickable: true,
  zIndex: 1,
});

const buildCircleOptions = (color: string, editable: boolean) => ({
  fillColor: color,
  fillOpacity: 0.3,
  strokeColor: color,
  strokeOpacity: 0.9,
  strokeWeight: 2,
  editable,
  draggable: editable,
  clickable: true,
  zIndex: 1,
});

const draftOverride = {
  fillOpacity: 0.15,
  strokeOpacity: 0.6,
  editable: false,
  draggable: false,
  clickable: false,
};

const polygonPathToCoords = (polygon: google.maps.Polygon): LatLng[] => {
  const path = polygon.getPath();
  const coords: LatLng[] = [];
  for (let i = 0; i < path.getLength(); i++) {
    const ll = path.getAt(i);
    coords.push({ lat: ll.lat(), lng: ll.lng() });
  }
  return coords;
};

// Haversine distance in meters. Avoids depending on the Maps `geometry`
// library so the loader doesn't have to fetch it.
const haversineMeters = (a: LatLng, b: LatLng): number => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

export const GeofenceDrawingMap: React.FC<GeofenceDrawingMapProps> = ({
  shapeType,
  initialValue,
  color = DEFAULT_GEOFENCE_COLOR,
  suggestedCenter,
  panTo,
  onChange,
  size = { width: '100%', height: '420px' },
}) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);

  // Committed shape — what the user has finalized (either the initial value or
  // a freshly drawn shape). When non-null, renders as an editable Polygon or
  // Circle that the user can drag/reshape. Going back to null happens via Reset.
  const [committed, setCommitted] = useState<DrawnShapeValue | null>(
    initialValue ?? null,
  );

  // Draft state — accumulates while the user is actively drawing a new shape
  // and is discarded the moment it commits (or Reset is hit). Draft polygon is
  // the partial vertex list; draft circle is the in-progress center+radius
  // preview during the 2nd click + mousemove.
  const [draftPolygon, setDraftPolygon] = useState<LatLng[]>([]);
  const [draftCircle, setDraftCircle] = useState<{
    center: LatLng;
    radius: number;
  } | null>(null);

  // Tracks whether the GoogleMap has fully mounted. The shape Polygon/Circle
  // must wait for this — otherwise on the very first open (when the Maps API
  // is still loading), the shape can mount before the map is ready and never
  // attach. Subsequent opens work because the API stays cached.
  const [isMapReady, setIsMapReady] = useState(false);

  const hasShape = committed !== null;
  const isDrawingPolygon = !hasShape && shapeType === GeofenceShapeType.POLYGON;
  const isDrawingCircle =
    !hasShape && shapeType === GeofenceShapeType.POINT_RADIUS;
  const circleStep: 'idle' | 'awaiting-radius' = draftCircle
    ? 'awaiting-radius'
    : 'idle';

  // ===== Value emission =====

  const emitPolygon = useCallback(
    (polygon: google.maps.Polygon) => {
      const coords = polygonPathToCoords(polygon);
      if (coords.length < 3) return;
      onChange({
        shapeType: GeofenceShapeType.POLYGON,
        polygonCoordinates: coords,
      });
    },
    [onChange],
  );

  const emitCircle = useCallback(
    (circle: google.maps.Circle) => {
      const center = circle.getCenter();
      const radius = circle.getRadius();
      if (!center) return;
      onChange({
        shapeType: GeofenceShapeType.POINT_RADIUS,
        centerPoint: { lat: center.lat(), lng: center.lng() },
        radiusMeters: Math.round(radius),
      });
    },
    [onChange],
  );

  // ===== Listeners on committed shapes (so dragging/editing emits) =====

  const attachPolygonListeners = useCallback(
    (polygon: google.maps.Polygon) => {
      const path = polygon.getPath();
      const handler = () => emitPolygon(polygon);
      const listeners: google.maps.MapsEventListener[] = [
        google.maps.event.addListener(path, 'set_at', handler),
        google.maps.event.addListener(path, 'insert_at', handler),
        google.maps.event.addListener(path, 'remove_at', handler),
        google.maps.event.addListener(polygon, 'dragend', handler),
      ];
      return () => listeners.forEach((l) => google.maps.event.removeListener(l));
    },
    [emitPolygon],
  );

  const attachCircleListeners = useCallback(
    (circle: google.maps.Circle) => {
      const handler = () => emitCircle(circle);
      const listeners: google.maps.MapsEventListener[] = [
        google.maps.event.addListener(circle, 'center_changed', handler),
        google.maps.event.addListener(circle, 'radius_changed', handler),
        google.maps.event.addListener(circle, 'dragend', handler),
      ];
      return () => listeners.forEach((l) => google.maps.event.removeListener(l));
    },
    [emitCircle],
  );

  const onCommittedPolygonLoad = useCallback(
    (polygon: google.maps.Polygon) => {
      polygonRef.current = polygon;
      attachPolygonListeners(polygon);
      // Echo so the form's drawnShape is populated even if the user clicks Save
      // without further edits.
      emitPolygon(polygon);
    },
    [attachPolygonListeners, emitPolygon],
  );

  const onCommittedCircleLoad = useCallback(
    (circle: google.maps.Circle) => {
      circleRef.current = circle;
      attachCircleListeners(circle);
      emitCircle(circle);
    },
    [attachCircleListeners, emitCircle],
  );

  // ===== Drawing handlers (replace the old DrawingManager) =====

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      if (isDrawingPolygon) {
        setDraftPolygon((prev) => [...prev, point]);
        return;
      }
      if (isDrawingCircle) {
        if (circleStep === 'idle') {
          setDraftCircle({ center: point, radius: 0 });
          return;
        }
        // Second click — commit the circle.
        if (!draftCircle) return;
        const radius = Math.max(
          1,
          Math.round(haversineMeters(draftCircle.center, point)),
        );
        setCommitted({
          shapeType: GeofenceShapeType.POINT_RADIUS,
          centerPoint: draftCircle.center,
          radiusMeters: radius,
        });
        setDraftCircle(null);
      }
    },
    [isDrawingPolygon, isDrawingCircle, circleStep, draftCircle],
  );

  const handleMapMouseMove = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!isDrawingCircle || circleStep !== 'awaiting-radius' || !draftCircle) return;
      if (!e.latLng) return;
      const cursor = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const radius = haversineMeters(draftCircle.center, cursor);
      setDraftCircle({ center: draftCircle.center, radius });
    },
    [isDrawingCircle, circleStep, draftCircle],
  );

  const finishPolygonDraft = useCallback(() => {
    if (draftPolygon.length < 3) return;
    setCommitted({
      shapeType: GeofenceShapeType.POLYGON,
      polygonCoordinates: draftPolygon,
    });
    setDraftPolygon([]);
  }, [draftPolygon]);

  // ===== Map lifecycle =====

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      setIsMapReady(true);

      // When editing an existing shape, fit the map to its actual bounds so
      // the zoom is appropriate for the shape's size (a 100m circle and a
      // city-block polygon need very different zooms).
      if (initialValue) {
        const bounds = new window.google.maps.LatLngBounds();
        if (
          initialValue.shapeType === GeofenceShapeType.POINT_RADIUS &&
          initialValue.centerPoint &&
          initialValue.radiusMeters
        ) {
          const { lat, lng } = initialValue.centerPoint;
          const r = initialValue.radiusMeters;
          const latDelta = r / 111320;
          const lngDelta = r / (111320 * Math.cos((lat * Math.PI) / 180));
          bounds.extend(new window.google.maps.LatLng(lat - latDelta, lng - lngDelta));
          bounds.extend(new window.google.maps.LatLng(lat + latDelta, lng + lngDelta));
        } else if (
          initialValue.shapeType === GeofenceShapeType.POLYGON &&
          initialValue.polygonCoordinates
        ) {
          initialValue.polygonCoordinates.forEach((p) => {
            bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
          });
        }
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, 50);
          return;
        }
      }

      // No existing shape: focus on a suggestion (project/warehouse coords or
      // an address picked from the search input).
      if (suggestedCenter) {
        map.setCenter(suggestedCenter);
        map.setZoom(NEW_SHAPE_ZOOM);
      } else {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
      }
    },
    [initialValue, suggestedCenter],
  );

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
    setIsMapReady(false);
  }, []);

  // ===== Clear =====

  const handleClear = useCallback(() => {
    polygonRef.current = null;
    circleRef.current = null;
    setCommitted(null);
    setDraftPolygon([]);
    setDraftCircle(null);
    onChange(null);
  }, [onChange]);

  // Reset shape state when shapeType changes — but skip the first run so the
  // initial value (when editing an existing geofence) isn't immediately wiped.
  const prevShapeTypeRef = useRef<GeofenceShapeType | null>(null);
  useEffect(() => {
    if (prevShapeTypeRef.current === null) {
      prevShapeTypeRef.current = shapeType;
      return;
    }
    if (prevShapeTypeRef.current !== shapeType) {
      prevShapeTypeRef.current = shapeType;
      handleClear();
      // If the user toggled back to the geofence's original shape type, bring
      // the initial shape back so they don't lose visual context after a
      // round-trip toggle (e.g. Circle → Polygon → Circle).
      if (initialValue && shapeType === initialValue.shapeType) {
        setCommitted(initialValue);
      }
    }
  }, [shapeType, initialValue, handleClear]);

  // Pan to external coordinates (address search, project/warehouse selection).
  const panToLat = panTo?.lat;
  const panToLng = panTo?.lng;
  useEffect(() => {
    if (panToLat == null || panToLng == null || !mapRef.current) return;
    mapRef.current.panTo({ lat: panToLat, lng: panToLng });
    const currentZoom = mapRef.current.getZoom() ?? 0;
    if (currentZoom < NEW_SHAPE_ZOOM - 2) {
      mapRef.current.setZoom(NEW_SHAPE_ZOOM);
    }
  }, [panToLat, panToLng]);

  const draftPolygonOptions = useMemo(
    () => ({ ...buildPolygonOptions(color, false), ...draftOverride }),
    [color],
  );
  const draftCircleOptions = useMemo(
    () => ({ ...buildCircleOptions(color, false), ...draftOverride }),
    [color],
  );

  if (!isLoaded) {
    return <div style={size} className="bg-gray-50 animate-pulse rounded-lg" />;
  }

  const showResetButton =
    hasShape || draftPolygon.length > 0 || !!draftCircle;
  const showFinishPolygonButton =
    isDrawingPolygon && draftPolygon.length >= 3;

  return (
    <div className="relative" style={size}>
      <div className="absolute top-2 left-2 z-10 flex items-center gap-2 bg-white rounded-md shadow px-3 py-2 text-xs text-gray-700">
        {shapeType === GeofenceShapeType.POLYGON ? (
          <>
            <PolylineIcon sx={{ fontSize: 16 }} className="text-purple-600" />
            <span>
              {hasShape
                ? 'Drag vertices to adjust'
                : draftPolygon.length < 3
                ? `Click to add points (${draftPolygon.length}/3 min)`
                : 'Click to add more points, or Finish to close the polygon'}
            </span>
          </>
        ) : (
          <>
            <RoomIcon sx={{ fontSize: 16 }} className="text-blue-600" />
            <span>
              {hasShape
                ? 'Drag center or edge to adjust'
                : circleStep === 'idle'
                ? 'Click to set the center'
                : 'Move the cursor to size, click to confirm'}
            </span>
          </>
        )}
      </div>

      <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
        {showFinishPolygonButton && (
          <button
            type="button"
            onClick={finishPolygonDraft}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 rounded-md shadow text-xs font-medium text-white hover:bg-indigo-700"
          >
            <CheckIcon sx={{ fontSize: 16 }} />
            Finish
          </button>
        )}
        {showResetButton && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-md shadow text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <RestartAltIcon sx={{ fontSize: 16 }} />
            Reset
          </button>
        )}
      </div>

      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        onClick={handleMapClick}
        onMouseMove={handleMapMouseMove}
        options={{
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {/* Committed shape — initial-from-prop or freshly drawn. Editable so
            the user can fine-tune via vertex/edge handles. Gated on
            isMapReady so on a cold Maps load the shape doesn't race the map. */}
        {isMapReady &&
          committed?.shapeType === GeofenceShapeType.POLYGON &&
          committed.polygonCoordinates && (
            <Polygon
              paths={committed.polygonCoordinates}
              options={buildPolygonOptions(color, true)}
              onLoad={onCommittedPolygonLoad}
            />
          )}
        {isMapReady &&
          committed?.shapeType === GeofenceShapeType.POINT_RADIUS &&
          committed.centerPoint &&
          committed.radiusMeters && (
            <Circle
              center={committed.centerPoint}
              radius={committed.radiusMeters}
              options={buildCircleOptions(color, true)}
              onLoad={onCommittedCircleLoad}
            />
          )}

        {/* Draft preview while the user is actively drawing. Non-editable,
            lower opacity, no listeners — just visual feedback. */}
        {isMapReady && isDrawingPolygon && draftPolygon.length > 0 && (
          <Polygon paths={draftPolygon} options={draftPolygonOptions} />
        )}
        {isMapReady &&
          isDrawingCircle &&
          draftCircle &&
          draftCircle.radius > 0 && (
            <Circle
              center={draftCircle.center}
              radius={draftCircle.radius}
              options={draftCircleOptions}
            />
          )}
      </GoogleMap>
    </div>
  );
};

export default GeofenceDrawingMap;
